import React, { useEffect, useState } from 'react';
import { FiX, FiDownload, FiPrinter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from './SpinnerCargando';
import { useImpresion } from '../../contexts/ContextoImpresion';

export default function ModalPDFBase64({
  mostrar,
  onClose,
  idFactura,
  idCompra,
  idCotizacion,
  pdfBase64Directo = null,
  ncf: ncfProp = '',
  tituloDocumento = 'Factura',
  formato = 'carta',
  tipoDocumento = null,
  idDocumento = null
}) {
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [ncf, setNcf] = useState('');

  const { qzConectado, imprimirDocumento, impresoraTermica } = useImpresion();

  const effectiveIdDocumento = idDocumento || idFactura || idCompra || idCotizacion;
  const effectiveTipoDocumento = tipoDocumento || (idCompra ? 'compra' : idFactura ? 'factura' : idCotizacion ? 'cotizacion' : null);

  // Convertir base64 a Blob URL cuando cambia pdfBase64
  useEffect(() => {
    if (!pdfBase64) {
      setPdfBlobUrl(null);
      return;
    }

    try {
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      console.log('[ModalPDFBase64] Blob URL creado:', { blobSize: blob.size, url });
      setPdfBlobUrl(url);

      // Limpiar cuando el componente se desmonta
      return () => {
        console.log('[ModalPDFBase64] Revocando Blob URL');
        URL.revokeObjectURL(url);
      };
    } catch (err) {
      console.error('[ModalPDFBase64] Error al crear Blob URL:', err);
      setError('Error al procesar el PDF');
      setPdfBlobUrl(null);
    }
  }, [pdfBase64]);

  useEffect(() => {
    if (pdfBase64Directo) {
      console.log('[ModalPDFBase64] pdfBase64Directo recibido:', {
        length: pdfBase64Directo.length,
        primeros20chars: pdfBase64Directo.substring(0, 20),
        ultimos20chars: pdfBase64Directo.substring(pdfBase64Directo.length - 20)
      });
      setPdfBase64(pdfBase64Directo);
      if (ncfProp) setNcf(ncfProp);
      return;
    }

    if (mostrar && (idFactura || idCompra || idCotizacion)) {
      cargarPDF();
    }

    return () => {
      if (!pdfBase64Directo) {
        setPdfBase64(null);
        setError(null);
      }
    };
  }, [mostrar, idFactura, idCompra, idCotizacion, pdfBase64Directo, ncfProp]);

  async function cargarPDF() {
    setCargando(true);
    setError(null);
    try {
      let resp;

      if (idCompra) {
        resp = await instanciaAxios.get(`/compra/${idCompra}/pdf`);
      } else if (idFactura) {
        resp = await instanciaAxios.get(`/factura/${idFactura}/pdf`);
      } else if (idCotizacion) {
        resp = await instanciaAxios.get(`/cotizacion/${idCotizacion}/pdf`);
      } else {
        throw new Error('No se especificó ID de factura, compra ni cotización');
      }
      
      if (resp.data && resp.data.data && resp.data.data.pdf_base64) {
        setPdfBase64(resp.data.data.pdf_base64);
        if (resp.data.data.ncf) {
          setNcf(resp.data.data.ncf);
        }
      } else {
        throw new Error('Respuesta del servidor sin PDF');
      }
    } catch (err) {
      console.error('Error al cargar PDF:', err);
      setError('No se pudo cargar el PDF');
      toast.error(`Error al cargar el PDF del documento`);
    } finally {
      setCargando(false);
    }
  }

  const handleDescargar = () => {
    if (!pdfBase64) return;
    
    try {
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tituloDocumento.toLowerCase().replace(/\s+/g, '_')}-${ncf || idFactura || idCompra || idCotizacion}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      toast.error('Error al descargar el PDF');
    }
  };

  const handleImprimir = () => {
    if (!pdfBase64) return;

    try {
      // Convertir base64 a Blob (más compatible que data URL para PDFs grandes)
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      // Crear iframe oculto para imprimir (no abre nueva ventana)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = blobUrl;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.error('Error al llamar print:', e);
            toast.error('Error al abrir diálogo de impresión');
          }
        }, 100);

        // Limpiar iframe y Blob URL después de un tiempo
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 60000);
      };

      iframe.onerror = () => {
        toast.error('Error al cargar el PDF para imprimir');
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      };

    } catch (error) {
      console.error('Error al imprimir PDF:', error);
      toast.error('Error al imprimir el PDF');
    }
  };

  const handleClose = () => {
    setPdfBase64(null);
    setError(null);
    setNcf('');
    onClose();
  };

  if (!mostrar) return null;

  const mostrarIndicadorQZ = formato === 'continuo' && qzConectado && impresoraTermica;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Vista Previa de {tituloDocumento}
              {mostrarIndicadorQZ && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  QZ Tray
                </span>
              )}
            </h3>
            {ncf && (
              <p className="text-sm text-gray-600">
                NCF: {ncf}
              </p>
            )}
          </div>
          
          <div className="flex space-x-2">
            {pdfBase64 && (
              <>
                <button
                  onClick={handleDescargar}
                  className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  title="Descargar PDF"
                >
                  <FiDownload size={16} />
                  <span className="hidden sm:inline">Descargar</span>
                </button>
                
                <button
                  onClick={handleImprimir}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  title="Imprimir"
                >
                  <FiPrinter size={16} />
                  <span className="hidden sm:inline">Imprimir</span>
                </button>
              </>
            )}
            
            <button
              onClick={handleClose}
              className="flex items-center justify-center w-8 h-8 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 transition-colors"
              title="Cerrar"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>
        
        {/* PDF Content */}
        <div className="flex-1 p-4 overflow-hidden">
          {cargando ? (
            <div className="flex flex-col items-center justify-center h-full">
              <SpinnerCargando />
              <p className="text-gray-600 mt-4">Cargando PDF desde el servidor...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-center">
                <div className="text-red-600 text-5xl mb-4">⚠️</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Error al cargar PDF</h3>
                <p className="text-gray-600">{error}</p>
                <button
                  onClick={cargarPDF}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          ) : pdfBlobUrl ? (
            <div className="w-full h-full border border-gray-300 rounded-md overflow-hidden">
              <iframe
                src={pdfBlobUrl}
                className="w-full h-full"
                title={`Vista previa de ${tituloDocumento}`}
                onError={() => {
                  console.error('[ModalPDFBase64] Error al cargar PDF en iframe');
                  setError('El navegador no puede renderizar este PDF. Intenta descargarlo.');
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No hay PDF disponible</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex justify-between items-center text-sm text-gray-600">
            {(idFactura || idCompra || idCotizacion) && (
              <span>
                {tituloDocumento} ID: {idFactura || idCompra || idCotizacion}
              </span>
            )}
            {!idFactura && !idCompra && !idCotizacion && ncf && (
              <span>
                {tituloDocumento}: {ncf}
              </span>
            )}
            {!cargando && !error && pdfBase64 && (
              <span className="text-green-600 font-medium">
                ✓ PDF cargado correctamente
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
